import { useEffect, useRef } from "react";

interface UseLessonsToDoParams {
  fetchLessonsToDo: () => void | Promise<void>;
  focus: string | null;
  domain: string | null;
  debounceTime?: number;
  delayedFetchTime?: number;
}

export const useLessonsToDo = ({
  fetchLessonsToDo,
  focus,
  domain,
  debounceTime = 100,
  delayedFetchTime = 6000,
}: UseLessonsToDoParams): void => {
  const lessonsToDoTimeoutRef = useRef<number | null>(null);
  const lastFocusDomainRef = useRef<string | null>(null);
  const debounceTimeoutRef = useRef<number | null>(null);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    const currentFocusDomain = `${focus || ""}_${domain || ""}`;

    if (lastFocusDomainRef.current === currentFocusDomain) {
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = window.setTimeout(() => {
      lastFocusDomainRef.current = currentFocusDomain;

      if (lessonsToDoTimeoutRef.current) {
        clearTimeout(lessonsToDoTimeoutRef.current);
      }

      fetchLessonsToDo();

      if (!isInitialMountRef.current) {
        lessonsToDoTimeoutRef.current = window.setTimeout(() => {
          fetchLessonsToDo();
        }, delayedFetchTime);
      } else {
        isInitialMountRef.current = false;
      }
    }, debounceTime);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (lessonsToDoTimeoutRef.current) {
        clearTimeout(lessonsToDoTimeoutRef.current);
        lessonsToDoTimeoutRef.current = null;
      }
    };
  }, [focus, domain, fetchLessonsToDo, debounceTime, delayedFetchTime]);
};
